import type {
  EmailGetApiData,
  EmailGetApiResponses,
  EmailListApiData,
  EmailListApiResponses,
  EmailStatsApiData,
  EmailStatsApiResponses,
  ShortUrlClickLatencyStatsApiData,
  ShortUrlClickLatencyStatsApiResponses,
  ShortUrlCreateApiData,
  ShortUrlCreateApiResponses,
  ShortUrlGetApiData,
  ShortUrlGetApiResponses,
  ShortUrlListApiData,
  ShortUrlListApiResponses,
  ShortUrlStatsApiData,
  ShortUrlStatsApiResponses,
  SmsDepartmentCostExportApiData,
  SmsDepartmentCostExportApiResponses,
  SmsGetApiData,
  SmsGetApiResponses,
  SmsListApiData,
  SmsListApiResponses,
  SmsResendApiData,
  SmsResendApiResponses,
  SmsStatsApiData,
  SmsStatsApiResponses
} from '@navio/nsdk';

import { downloadToExcel } from '../../downloadToExcel';
import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';

type RecallClickLatencyStatsApiData = Omit<ShortUrlClickLatencyStatsApiData, 'query'> & {
  query?: NonNullable<ShortUrlClickLatencyStatsApiData['query']> & {
    recallSmsOnly?: boolean;
    serviceDepartmentId?: number;
    bookingProposalSmsSubjects?: string[];
  };
};

type SmsListApiDataWithRetryQueue = Omit<SmsListApiData, 'query'> & {
  query?: NonNullable<SmsListApiData['query']> & {
    unacceptedInRetryQueue?: boolean;
    unacceptedRetriesExhausted?: boolean;
  };
};

export const getSmsUrls = {
  getPaginatedSMS: dataEndpoint<SmsListApiDataWithRetryQueue, SmsListApiResponses>({
    getUrl: (queryParams) => apiUrl('sms/', queryParams)
  }),
  postSMSResend: responseEndpoint<SmsResendApiData, SmsResendApiResponses>({
    getUrl: ({ pk }) => apiUrl(`sms/${pk}/resend/`),
    getBody: ({ pk: _, receiverId }) => (receiverId != null ? { receiverId } : {})
  }),
  getEmail: dataEndpoint<EmailGetApiData, EmailGetApiResponses>({
    getUrl: ({ pk }) => apiUrl(`emails/${pk}/`)
  }),
  getEmailStats: dataEndpoint<EmailStatsApiData, EmailStatsApiResponses>({
    getUrl: (queryParams) => apiUrl('communication/emails/stats/', queryParams)
  }),
  getPaginatedEmails: dataEndpoint<EmailListApiData, EmailListApiResponses>({
    getUrl: (queryParams) => apiUrl('emails/', queryParams)
  }),
  getSmsStats: dataEndpoint<SmsStatsApiData, SmsStatsApiResponses>({
    getUrl: (queryParams) => apiUrl('communication/sms/stats/', queryParams)
  }),
  getSmsDepartmentCostExport: dataEndpoint<SmsDepartmentCostExportApiData, SmsDepartmentCostExportApiResponses>({
    getUrl: (queryParams) => apiUrl('communication/sms/costs/export/', queryParams),
    handleRes: async (res) => downloadToExcel(res),
    responseType: 'blob',
    sideEffectGet: true
  }),
  getPaginatedSMSs: dataEndpoint<SmsListApiDataWithRetryQueue, SmsListApiResponses>({
    getUrl: (queryParams) => apiUrl('sms/', queryParams)
  }),
  getSMS: dataEndpoint<SmsGetApiData, SmsGetApiResponses>({
    getUrl: ({ pk }) => apiUrl(`sms/${pk}/`)
  }),
  getPaginatedShortUrls: dataEndpoint<ShortUrlListApiData, ShortUrlListApiResponses>({
    getUrl: (queryParams) => apiUrl('communication/short-urls/', queryParams)
  }),
  getShortUrl: dataEndpoint<ShortUrlGetApiData, ShortUrlGetApiResponses>({
    getUrl: ({ code }) => apiUrl(`communication/short-urls/${code}/`)
  }),
  getShortUrlStats: dataEndpoint<ShortUrlStatsApiData, ShortUrlStatsApiResponses>({
    getUrl: ({ code, ...queryParams }) => apiUrl(`communication/short-urls/${code}/stats/`, queryParams)
  }),
  getShortUrlClickLatencyStats: dataEndpoint<RecallClickLatencyStatsApiData, ShortUrlClickLatencyStatsApiResponses>({
    getUrl: (queryParams) => apiUrl('communication/short-urls/stats/click-latency/', queryParams)
  }),
  postShortUrl: responseEndpoint<ShortUrlCreateApiData, ShortUrlCreateApiResponses>({
    getUrl: () => apiUrl('communication/short-urls/'),
    getBody: (data) => data
  })
};
